import { useState } from 'react';

interface CalculatorInputProps {
  salary: number;
  frequency: 'annual' | 'monthly' | 'biweekly';
  filingStatus: string;
  showAdvanced: boolean;
  currencySymbol: string;
  onSalaryChange: (salary: number) => void;
  onFrequencyChange: (frequency: 'annual' | 'monthly' | 'biweekly') => void;
  onFilingStatusChange: (status: string) => void;
  onToggleAdvanced: () => void;
}

const CalculatorInput = ({
  salary,
  frequency,
  filingStatus,
  showAdvanced,
  currencySymbol,
  onSalaryChange,
  onFrequencyChange,
  onFilingStatusChange,
  onToggleAdvanced
}: CalculatorInputProps) => {
  const [inputValue, setInputValue] = useState(salary.toString());

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setInputValue(value);
    const numValue = parseInt(value) || 0;
    onSalaryChange(numValue);
  };

  const formatDisplayValue = () => {
    return salary.toLocaleString();
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Enter Your Salary</h2>

      {/* Salary Input */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Gross Salary
        </label>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-gray-400">
            {currencySymbol}
          </span>
          <input
            type="text"
            value={formatDisplayValue()}
            onChange={handleInputChange}
            className="w-full pl-12 pr-4 py-4 text-3xl font-semibold border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none transition-colors"
            placeholder="75,000"
          />
        </div>
      </div>

      {/* Frequency Toggle */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Payment Frequency
        </label>
        <div className="inline-flex rounded-lg border border-gray-300 p-1 bg-gray-50">
          <button
            onClick={() => onFrequencyChange('annual')}
            className={`px-6 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
              frequency === 'annual'
                ? 'bg-gray-900 text-white shadow-sm'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            Annual
          </button>
          <button
            onClick={() => onFrequencyChange('monthly')}
            className={`px-6 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
              frequency === 'monthly'
                ? 'bg-gray-900 text-white shadow-sm'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => onFrequencyChange('biweekly')}
            className={`px-6 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
              frequency === 'biweekly'
                ? 'bg-gray-900 text-white shadow-sm'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            Biweekly
          </button>
        </div>
      </div>

      {/* Advanced Options */}
      <div className="border-t border-gray-200 pt-6">
        <button
          onClick={onToggleAdvanced}
          className="flex items-center justify-between w-full text-left text-gray-700 hover:text-gray-900 transition-colors cursor-pointer"
        >
          <span className="flex items-center space-x-2">
            <i className="ri-settings-3-line text-lg"></i>
            <span className="font-medium">Advanced Settings</span>
          </span>
          <i className={`ri-arrow-${showAdvanced ? 'up' : 'down'}-s-line text-xl transition-transform`}></i>
        </button>

        {showAdvanced && (
          <div className="mt-6 space-y-4 animate-fadeIn">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filing Status
              </label>
              <select
                value={filingStatus}
                onChange={(e) => onFilingStatusChange(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors cursor-pointer"
              >
                <option value="single">Single</option>
                <option value="married">Married Filing Jointly</option>
                <option value="married_separate">Married Filing Separately</option>
                <option value="head">Head of Household</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Number of Dependents
              </label>
              <input
                type="number"
                min="0"
                defaultValue="0"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pre-tax Deductions (401k, HSA, etc.)
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                  {currencySymbol}
                </span>
                <input
                  type="text"
                  placeholder="0"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalculatorInput;